import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, RotateCcw } from 'lucide-react';
import { Button, Spinner } from '@/components/ui';

interface QueryErrorStateProps {
  /** The error object from TanStack Query */
  error: unknown;
  /** Human-readable label for what failed (e.g. "loans", "dashboard") */
  subject?: string;
  /** Called when user clicks Retry — typically refetch() from the query */
  onRetry?: () => void;
}

/**
 * Extracts a meaningful message from an API error.
 * Prioritises the server's error message over generic fallbacks.
 */
function getErrorDetail(error: unknown): string {
  if (!error) return 'An unexpected error occurred.';

  // Axios error with response
  const axiosErr = error as any;
  if (axiosErr?.response?.data?.message) {
    return axiosErr.response.data.message;
  }
  if (axiosErr?.response?.status === 403) {
    return 'You don\u2019t have permission to access this resource.';
  }
  if (axiosErr?.response?.status === 401) {
    return 'Your session has expired. Please log in again.';
  }
  if (axiosErr?.response?.status >= 500) {
    return 'The server encountered an error. Please try again shortly.';
  }
  if (axiosErr?.code === 'ERR_NETWORK' || axiosErr?.message?.includes('Network Error')) {
    return 'Unable to connect to the server. Check your network connection.';
  }

  // Generic Error
  if (error instanceof Error) return error.message;

  return 'An unexpected error occurred.';
}

/**
 * Reusable error state for TanStack Query failures.
 * Shows the API's error message, a Retry button, and a Reload App fallback.
 */
export function QueryErrorState({ error, subject, onRetry }: QueryErrorStateProps) {
  const qc = useQueryClient();

  const detail = getErrorDetail(error);
  const label = subject ? `Failed to load ${subject}` : 'Failed to load data';

  function handleRetry() {
    if (onRetry) {
      onRetry();
    } else {
      // Clear all stale queries and refetch
      qc.invalidateQueries();
    }
  }

  function handleReloadApp() {
    // Clear React Query cache completely, then hard reload
    qc.clear();
    window.location.reload();
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
        <AlertCircle className="w-6 h-6 text-red-500" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">{label}</h3>
      <p className="text-sm text-gray-500 mb-6 max-w-md">{detail}</p>
      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          onClick={handleRetry}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium hover: transition-colors"
        >
          <Spinner size="sm" className="w-4 h-4" />
          Retry
        </Button>
        <button
          onClick={handleReloadApp}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Reload App
        </button>
      </div>
    </div>
  );
}
